<section>
  <h1 class="text-center text-3xl font-bold md:text-4xl">
    iOS Certificate Generator
  </h1>
  <p class="mx-auto mt-4 text-balance text-center text-xl">
    Generate signing certificates for your iOS app directly in your browser — no
    data is sent to any server.
  </p>
  <nav
    tuiStepper
    [activeItemIndex]="activeStepIndex()"
    class="mx-auto mt-12 w-full md:w-2/3 lg:w-1/2"
  >
    <button tuiStep [stepState]="step1State()">
      Download Certificate Request
    </button>
    <button tuiStep [disabled]="!privateKey()" [stepState]="step2State()">
      Download Certificate Bundle
    </button>
  </nav>
  @if (activeStepIndex() === 0) {
    <form
      [formGroup]="csrFormGroup"
      (ngSubmit)="onGenerateCsrSubmit()"
      class="mx-auto mt-12 w-full md:w-2/3 lg:w-1/2"
    >
      <div class="tui-form__row flex flex-col gap-4 md:flex-row">
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>Full Name <span class="tui-required"></span></label>
            <input
              [formControl]="csrFormGroup.controls.fullName"
              tuiTextfield
              placeholder="John Doe"
            />
            <tui-icon tuiHintDirection="top-left" [tuiTooltip]="fullNameHint" />
          </tui-textfield>
          <ng-template #fullNameHint>
            The name that will appear in the certificate's Common Name (CN)
            field.
          </ng-template>
          <tui-error
            formControlName="fullName"
            [error]="[] | tuiFieldError | async"
          />
        </div>
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>Email <span class="tui-required"></span></label>
            <input
              [formControl]="csrFormGroup.controls.email"
              tuiTextfield
              placeholder="john@example.com"
            />
            <tui-icon tuiHintDirection="top-left" [tuiTooltip]="emailHint" />
          </tui-textfield>
          <ng-template #emailHint>
            The email address that will be embedded in the certificate.
          </ng-template>
          <tui-error
            formControlName="email"
            [error]="[] | tuiFieldError | async"
          />
        </div>
      </div>
      <div class="tui-form__row flex flex-col gap-4 md:flex-row">
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>Organization</label>
            <input
              [formControl]="csrFormGroup.controls.organization"
              tuiTextfield
              placeholder="Acme Inc."
            />
            <tui-icon [tuiTooltip]="organizationHint" />
          </tui-textfield>
          <ng-template #organizationHint>
            The legal name of your company or organization.
          </ng-template>
          <tui-error
            formControlName="organization"
            [error]="[] | tuiFieldError | async"
          />
        </div>
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>Organizational Unit</label>
            <input
              [formControl]="csrFormGroup.controls.organizationalUnit"
              tuiTextfield
              placeholder="Engineering"
            />
            <tui-icon [tuiTooltip]="organizationalUnitHint" />
          </tui-textfield>
          <ng-template #organizationalUnitHint>
            The department or division within your organization.
          </ng-template>
          <tui-error
            formControlName="organizationalUnit"
            [error]="[] | tuiFieldError | async"
          />
        </div>
      </div>
      <div class="tui-form__row flex flex-col gap-4 md:flex-row">
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>City</label>
            <input
              [formControl]="csrFormGroup.controls.city"
              tuiTextfield
              placeholder="San Francisco"
            />
            <tui-icon tuiHintDirection="top-left" [tuiTooltip]="cityHint" />
          </tui-textfield>
          <ng-template #cityHint>
            The city where your organization is located.
          </ng-template>
          <tui-error
            formControlName="city"
            [error]="[] | tuiFieldError | async"
          />
        </div>
        <div class="flex-1">
          <tui-textfield>
            <label tuiLabel>State</label>
            <input
              [formControl]="csrFormGroup.controls.state"
              tuiTextfield
              placeholder="California"
            />
            <tui-icon tuiHintDirection="top-left" [tuiTooltip]="stateHint" />
          </tui-textfield>
          <ng-template #stateHint>
            The state or province where your organization is located.
          </ng-template>
          <tui-error
            formControlName="state"
            [error]="[] | tuiFieldError | async"
          />
        </div>
      </div>
      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel>Country (2-letter code)</label>
          <input
            [formControl]="csrFormGroup.controls.country"
            tuiTextfield
            maxlength="2"
            placeholder="US"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="countryHint" />
        </tui-textfield>
        <ng-template #countryHint>
          The two-letter ISO 3166-1 alpha-2 country code.
        </ng-template>
        <tui-error
          formControlName="country"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="tui-form__row">
        <label tuiLabel class="flex items-center gap-2">
          <input
            formControlName="newsletter"
            tuiCheckbox
            type="checkbox"
            [size]="'m'"
          />
          <span class="tui-text_body-m">
            Subscribe to the Capawesome Newsletter (no spam, promise!)
          </span>
        </label>
      </div>
      <div class="tui-form__row">
        <label tuiLabel class="flex items-center gap-2">
          <input
            formControlName="privacy"
            tuiCheckbox
            type="checkbox"
            [size]="'m'"
          />
          <span class="tui-text_body-m">
            I've read and accept the
            <a
              href="https://capawesome.io/privacy/"
              target="_blank"
              class="text-[color:var(--tui-text-action)]"
              >Privacy Policy</a
            >.
            <span class="tui-required"></span>
          </span>
        </label>
        <tui-error
          formControlName="privacy"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="tui-form__row">
        <button
          tuiButton
          type="submit"
          [loading]="loading()"
          class="w-full"
          data-umami-event="tools-ios-certificate-generator-download-certificate-request-button"
        >
          Download Certificate Request
        </button>
      </div>
    </form>
  }
  @if (activeStepIndex() === 1) {
    <form
      [formGroup]="p12FormGroup"
      (ngSubmit)="onGenerateP12Submit()"
      class="mx-auto mt-12 w-full md:w-2/3 lg:w-1/2"
    >
      <p class="tui-text_body-m tui-form__row">
        Next, go to Apple's
        <a
          href="https://developer.apple.com/account/resources/certificates/list"
          target="_blank"
          class="text-[color:var(--tui-text-action)]"
          >Certificates, Identifiers & Profiles</a
        >
        page and click the "+" button to <b>add a new certificate</b>. Select
        "Apple Development" or "Apple Distribution" as the type. Click
        "Continue" and then <b>upload the CSR file</b> you generated in the
        previous step. After uploading,
        <b>download the generated .cer file</b> and upload it below.
      </p>
      <div class="tui-form__row">
        <label tuiLabel
          >Public Certificate (.cer) <span class="tui-required"></span
        ></label>
        @if (rejectedFile(); as rejectedFile) {
          <tui-file
            state="error"
            [file]="rejectedFile"
            (remove)="onCertificateFileRemoveClick()"
            class="mt-2"
          />
        } @else if (p12FormGroup.controls.certificateFile.value) {
          <tui-file
            [file]="p12FormGroup.controls.certificateFile.value"
            (remove)="onCertificateFileRemoveClick()"
            class="mt-2"
          />
        } @else {
          <label tuiInputFiles class="mt-2">
            <input
              tuiInputFiles
              accept=".cer"
              [formControl]="p12FormGroup.controls.certificateFile"
            />
          </label>
        }
      </div>
      <p class="tui-text_body-m tui-form__row">
        Finally, <b>enter a password</b> to protect your certificate bundle.
        After you click "Download Certificate Bundle", you'll get a .p12 file
        that you can use for code signing in your CI/CD pipeline or local
        machine.
      </p>
      <div class="tui-form__row">
        <tui-textfield>
          <label tuiLabel>Password <span class="tui-required"></span></label>
          <input
            [formControl]="p12FormGroup.controls.password"
            tuiTextfield
            type="password"
          />
          <tui-icon [tuiTooltip]="p12PasswordHint" />
        </tui-textfield>
        <ng-template #p12PasswordHint>
          Used to protect the .p12 certificate bundle. Required when configuring
          code signing.
        </ng-template>
        <tui-error
          formControlName="password"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="tui-form__row">
        <button
          tuiButton
          type="submit"
          [loading]="loading()"
          class="w-full"
          data-umami-event="tools-ios-certificate-generator-download-certificate-bundle-button"
        >
          Download Certificate Bundle
        </button>
      </div>
    </form>
  }
</section>
